import { PlusOutlined } from "@ant-design/icons"
import { COLOR } from "@finway-group/shared/lib/consts"
import { DeliveryNoteLineItem } from "@finway-group/shared/lib/models/purchaseOrder/lineItem.interface"
import { LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES } from "@finway-group/shared/lib/models/purchaseOrder/lineItem.validation"
import { calculateTotalQuantityReceived } from "@finway-group/shared/lib/utils/purchaseOrder.util"
import * as Sentry from "@sentry/react"
import { Button, Modal, Tag, Tooltip } from "antd"
import * as dinero from "dinero.js"
import { TFunction } from "i18next"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { LinkIcon } from "~Components/icons/LinkIcon"
import { PurchaseOrderTagIcon } from "~Components/icons/PurchaseOrderTagIcon"
import { UnlinkIcon } from "~Components/icons/UnlinkIcon"
import { Loading } from "~Components/loading"
import { useDeliveryNoteQuery } from "~Features/pages/deliveryNotes/queries/deliveryNote.queries"
import { defaultFilterDeliveryNoteLink } from "~Features/pages/purchaseOrders/queries/const"
import { usePurchaseOrdersForLinkingQuery } from "~Features/pages/purchaseOrders/queries/purchaseOrder.queries"
import { PurchaseOrder } from "~Features/pages/purchaseOrders/types/PurchaseOrder.interfaces"
import { CancelableComponent, ModalComponentPropsBase } from "~Shared/context/modal.context"
import { PurchaseOrderReference } from "~Shared/models/purchaseOrderReference"
import { LineItemCard } from "../PurchaseOrderLink/LineItemCard"
import { decorateItemsWithIndex } from "../PurchaseOrderLink/useLinkPurchaseOrder.hook"
import { PurchaseOrderSelectionModal } from "../PurchaseOrderSelection.modal"
import { useLinkDeliveryNote, type LinkedPair } from "./useLinkDeliveryNote.hook"

interface DeliveryNoteLinkModalProps extends ModalComponentPropsBase, Required<CancelableComponent> {
    deliveryNoteId: string
    selectedPurchaseOrders: Array<PurchaseOrderReference>
}

const poItemFields = (item: PurchaseOrder["lineItems"][number], t: TFunction) => {
    const quantityReceivedRaw = calculateTotalQuantityReceived(item) ?? 0

    const quantityReceived = dinero
        .default({ amount: Math.round(quantityReceivedRaw * 10 ** LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES), precision: LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES })
        .toRoundedUnit(LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES)
    const openQuantityRaw = (item.quantity ?? 0) - (calculateTotalQuantityReceived(item) ?? 0)
    const openQuantity = dinero
        .default({ amount: Math.round(openQuantityRaw * 10 ** LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES), precision: LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES })
        .toRoundedUnit(LINE_ITEM_QUANTITY_MAX_DECIMAL_PLACES)
    return [
        { label: t("label:po_link.quantity_ordered"), value: item.quantity ?? "", key: "quantityOrdered" },
        { label: t("label:po_link.previously_received"), value: quantityReceived, key: "previouslyReceived" },
        {
            label: t("label:po_link.open_quantity"),
            value: openQuantity,
            key: "openQuantity",
        },
    ]
}

const deliveryNoteItemFields = (item: DeliveryNoteLineItem<string>, t: TFunction) => [
    { label: t("label:po_link.quantity"), value: `${item.quantity} ${item.unitOfMeasure ? `(${item.unitOfMeasure})` : ""}`, key: "quantity" },
]

export const DeliveryNoteLinkModal = ({ deliveryNoteId, isShowing, onCancel, selectedPurchaseOrders }: DeliveryNoteLinkModalProps) => {
    const { t } = useTranslation()
    const [supposedActivePOId, setSupposedActivePOId] = useState<string>(selectedPurchaseOrders[0]?._id || "")
    const [additionalPurchaseOrders, setAdditionalPurchaseOrders] = useState<Array<PurchaseOrderReference>>([])
    const [isAdditionalModalOpen, setIsAdditionalModalOpen] = useState<boolean>(false)
    const { data: deliveryNote, isLoading: isDeliveryNoteLoading } = useDeliveryNoteQuery(deliveryNoteId)
    const purchaseOrderReferences = [...selectedPurchaseOrders, ...additionalPurchaseOrders]
    const { data, isLoading: isPurchaseOrdersLoading } = usePurchaseOrdersForLinkingQuery({ filter: { _id: { in: purchaseOrderReferences.map((po) => po._id) } } })
    const purchaseOrders = data?.docs || []
    const {
        deliveryNoteLineItems,
        handleDeliveryNoteItemClick,
        handlePurchaseOrderItemClick,
        removeLink,
        clearAllLinks,
        isDeliveryNoteItemLinked,
        isPurchaseOrderItemLinked,
        isItemActive,
        isPurchaseOrderItemDisabled,
        saveLinkChanges,
        hasChanges,
        linkedPairs,
        isPoLinkingDisabled,
        isClearAllDisabled,
        isLinkMutationLoading,
    } = useLinkDeliveryNote(deliveryNote, purchaseOrders)
    if (isDeliveryNoteLoading || isPurchaseOrdersLoading) {
        return <Loading />
    }
    const decoratedSelectedPODatas = purchaseOrders?.map((po) => ({ ...po, lineItems: decorateItemsWithIndex(po.lineItems) })) || []
    const activePO = decoratedSelectedPODatas.find((po) => po._id === supposedActivePOId) || decoratedSelectedPODatas[0] // always have a default active PO
    const activePOId = activePO?._id
    // Debug early return conditions
    if (!deliveryNote || !deliveryNoteLineItems.length || !purchaseOrders?.length || !activePO) {
        onCancel()
        Sentry.captureException(
            `[Delivery Note Link Modal] Error: In delivery note: ${deliveryNoteId} trying to link purchase orders: ${purchaseOrders.map((po) => po._id).join(", ")} but found no purchase orders`,
        )
        return null
    }

    // Separate linked and unlinked items while preserving original order
    // For delivery note items: only show linked items if they're linked to the currently selected PO
    const linkedDeliveryNoteItemsForCurrentPO = deliveryNoteLineItems.filter((item) => {
        if (!isDeliveryNoteItemLinked(item?._id)) {
            return false
        }
        // Check if this item is linked to the currently selected PO
        const linkedPair = linkedPairs.find((pair: LinkedPair) => pair.deliveryNoteItemId === item._id!)
        return linkedPair?.poId === activePOId
    })

    const unlinkedDeliveryNoteItems = deliveryNoteLineItems.filter((item) => !isDeliveryNoteItemLinked(item?._id))
    const linkedPOItems = activePO.lineItems.filter((item: any) => isPurchaseOrderItemLinked(activePOId, item?._id))
    const unlinkedPOItems = activePO.lineItems.filter((item: any) => !isPurchaseOrderItemLinked(activePOId, item?._id))

    const onSubmit = async () => {
        await saveLinkChanges()
        onCancel()
    }

    const handleAdditionalPurchaseOrderSelection = (newPurchaseOrders: Array<PurchaseOrderReference>) => {
        const newPurchaseOrdersRefs = newPurchaseOrders.map((po) => ({
            _id: po._id,
            purchaseOrderNumber: po.purchaseOrderNumber,
            alreadyLinked: false,
        }))
        setAdditionalPurchaseOrders(newPurchaseOrdersRefs)
    }

    return (
        <>
            <Modal
                title=""
                open={isShowing}
                onCancel={onCancel}
                maskClosable={true}
                className="purchase-order-link-modal ant-modal--full-fit"
                footer={[
                    <Button key="cancel" onClick={onCancel}>
                        {t("action:cancel")}
                    </Button>,
                    <Button key="submit" type="primary" onClick={onSubmit} disabled={!hasChanges || isLinkMutationLoading} loading={isLinkMutationLoading}>
                        {t("action:po_link.submit_links")}
                    </Button>,
                ]}
            >
                <div className="h-full relative">
                    {/* Header row with both titles */}
                    <div className="mb-10" style={{ display: "grid", gridTemplateColumns: "1fr 100px 1fr", gap: "0px", height: "calc(100% - 100px)" }}>
                        <div>
                            <h3 className="text-lg font-medium">{t("label:po_link.delivery_note_line_items")}</h3>
                            <div className="flex justify-between">
                                <Tooltip
                                    title={
                                        <>
                                            <div className="font-bold text-white"> {t("label:po_link.auto_link_tooltip_title_coming_soon")} </div>
                                            <div> {t("label:po_link.auto_link_tooltip_coming_soon")}</div>
                                        </>
                                    }
                                >
                                    <Button size="small" icon={<LinkIcon color={COLOR.text} />} disabled>
                                        {t("action:po_link.auto_link_all")}
                                    </Button>
                                </Tooltip>
                                <Button size="small" icon={<UnlinkIcon color={COLOR.text} />} onClick={clearAllLinks} disabled={isClearAllDisabled}>
                                    {t("action:po_link.clear_all")}
                                </Button>
                            </div>
                        </div>
                        <div></div>
                        <div>
                            <h3 className="text-lg font-medium">{t("label:po_link.purchase_order_line_items")}</h3>
                            <div className="flex justify-between">
                                <div className="flex items-center gap-2 mt-2">
                                    {purchaseOrderReferences.map((po) => (
                                        <Tag
                                            key={po._id}
                                            onClick={() => {
                                                setSupposedActivePOId(po._id)
                                            }}
                                            style={{
                                                borderColor: activePOId === po._id ? COLOR.finway.green : "transparent",
                                                backgroundColor: activePOId === po._id ? "white" : "transparent",
                                            }}
                                            className={`mb-4 flex items-center align-middle cursor-pointer`}
                                        >
                                            <PurchaseOrderTagIcon className="mr-4" />
                                            {po.purchaseOrderNumber}
                                        </Tag>
                                    ))}
                                </div>
                                <Button icon={<PlusOutlined />} onClick={() => setIsAdditionalModalOpen(true)} />
                            </div>
                        </div>
                    </div>

                    {/* Content row with 3 columns */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 100px 1fr", gap: "0px", height: "calc(100% - 100px)" }}>
                        <div>
                            <div className="space-y-4">
                                {/* Linked items */}
                                {linkedDeliveryNoteItemsForCurrentPO.map((item: DeliveryNoteLineItem<string> & { index: number }, indx: number) => (
                                    <LineItemCard
                                        key={indx}
                                        index={item.index}
                                        materialCode={item.materialCode || ""}
                                        name={item.name || ""}
                                        fields={deliveryNoteItemFields(item, t)}
                                        isActive={isItemActive(item?._id)}
                                        isLinked={true}
                                        onClick={() => handleDeliveryNoteItemClick(item._id!)}
                                    />
                                ))}

                                {/* Unlinked items */}
                                {unlinkedDeliveryNoteItems.map((item: DeliveryNoteLineItem<string> & { index: number }, indx: number) => {
                                    const active = isItemActive(item?._id)

                                    return (
                                        <LineItemCard
                                            key={indx}
                                            index={item.index}
                                            materialCode={item.materialCode || ""}
                                            name={item.name || ""}
                                            fields={deliveryNoteItemFields(item, t)}
                                            isActive={active}
                                            isLinked={false}
                                            onClick={() => handleDeliveryNoteItemClick(item._id!)}
                                        />
                                    )
                                })}
                            </div>
                        </div>

                        {/* Middle column for connection lines */}
                        <div className="grid w-100" style={{ gridAutoRows: "1fr" }}>
                            {linkedPairs.map((pair, indx) => {
                                const deliveryNoteIndex = linkedDeliveryNoteItemsForCurrentPO.findIndex(
                                    (item: DeliveryNoteLineItem<string>) => item._id === pair.deliveryNoteItemId,
                                )
                                const purchaseIndex = linkedPOItems.findIndex((item: any) => item._id === pair.poItemId)

                                if (deliveryNoteIndex === -1 || purchaseIndex === -1) {
                                    return null
                                }

                                return (
                                    <div key={indx} className="flex flex-col items-center">
                                        <svg className="h-1 w-full relative" style={{ top: "50%" }}>
                                            <line x1="0" y1="0" x2="100%" y2="0" stroke={COLOR.finway.green} strokeWidth="2" strokeDasharray="5,5" />
                                        </svg>
                                        <div className="relative" style={{ top: "calc(50% - 16px)" }}>
                                            <Tooltip title={t("action:po_link.unlink")}>
                                                <UnlinkIcon
                                                    bgColor="#231C5B"
                                                    className={isPoLinkingDisabled(pair.poId) ? "cursor-not-allowed" : "cursor-pointer"}
                                                    onClick={() => removeLink(pair.deliveryNoteItemId, pair.poItemId, pair.poId)}
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                )
                            })}

                            {/* fill with placeholders for unlinked items */}
                            {(unlinkedDeliveryNoteItems.length > unlinkedPOItems.length ? unlinkedDeliveryNoteItems : unlinkedPOItems).map((_, indx: number) => (
                                <div key={`empty-${indx}`}></div>
                            ))}
                        </div>

                        {/* Right column for purchase order items */}
                        <div>
                            {activePO && (
                                <div className="space-y-4">
                                    {linkedPOItems.map((item: any & { index: number }, indx: number) => (
                                        <LineItemCard
                                            key={indx}
                                            index={item.index}
                                            materialCode={item.materialCode}
                                            name={item.name}
                                            fields={poItemFields(item, t)}
                                            isLinked={true}
                                            onClick={() => handlePurchaseOrderItemClick(item._id!, activePOId)}
                                        />
                                    ))}

                                    {unlinkedPOItems.map((item: any & { index: number }, indx: number) => {
                                        const active = isItemActive(item?._id)
                                        const disabled = isPurchaseOrderItemDisabled(activePOId, item._id!)

                                        return (
                                            <LineItemCard
                                                key={indx}
                                                index={item.index}
                                                materialCode={item.materialCode}
                                                name={item.name}
                                                fields={poItemFields(item, t)}
                                                isActive={active}
                                                isLinked={false}
                                                isDisabled={disabled}
                                                onClick={() => !disabled && handlePurchaseOrderItemClick(item._id!, activePOId)}
                                            />
                                        )
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
            <PurchaseOrderSelectionModal
                isShowing={isAdditionalModalOpen}
                onCancel={() => {
                    setIsAdditionalModalOpen(false)
                }}
                onSelection={handleAdditionalPurchaseOrderSelection}
                excludePurchaseOrderIds={selectedPurchaseOrders.map((po) => po._id)}
                defaultFilter={defaultFilterDeliveryNoteLink}
            />
        </>
    )
}
